import { Observable, of } from 'rxjs';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { PasswordPolicyDto } from '../../interfaces/password-policy-dto';

const emptyFn = () => {
    // NOOP
};

const minUpperCharRegex = /[A-Z]/g;
const minLowerCharRegex = /[a-z]/g;
const minNumericCharRegex = /\d/g;
const minSpecialCharRegex = /[#?!@$%^&*-]/g;

export const passwordRegexValidator = (passwordPatterns: () => Observable<PasswordPolicyDto>,
    totalValidationErrors: (errors: string[]) => void,
    beforeAction: () => void = emptyFn,
    afterAction: () => void = emptyFn,
    initialValue = ''
): AsyncValidatorFn =>
    (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
        if (!control.dirty || !control.value || !control.value?.length) {
            return of(null);
        }

        return control.valueChanges.pipe(
            filter(() => !!control.value?.length && control.value !== initialValue),
            debounceTime(500), distinctUntilChanged(), tap(beforeAction), switchMap(() => passwordPatterns()),
            map(pwdPattern => {
                const password = control.value;
                const passwordError: string[] = [];
                const minLengthRegex = new RegExp(`^.{${pwdPattern.length},}$`);
                if (pwdPattern.uppercase && (password?.match(minUpperCharRegex) ?? []).length < pwdPattern.uppercase) {
                    passwordError.push(`Password must contain at least ${pwdPattern.uppercase} uppercase characters`);
                }
                if (pwdPattern.lowercase && (password?.match(minLowerCharRegex) ?? []).length < pwdPattern.lowercase) {
                    passwordError.push(`Password must contain at least ${pwdPattern.lowercase} lowercase characters`);
                }
                if (pwdPattern.digits && (password?.match(minNumericCharRegex) ?? []).length < pwdPattern.digits) {
                    passwordError.push(`Password must contain at least ${pwdPattern.digits} numeric characters`);
                }
                if (pwdPattern.specialCharacters && (password?.match(minSpecialCharRegex) ?? []).length < pwdPattern.specialCharacters) {
                    passwordError.push(`Password must contain at least ${pwdPattern.specialCharacters} special symbol characters`);
                }
                if (pwdPattern.length && !minLengthRegex.test(password)) {
                    passwordError.push(`Password length must be at least ${pwdPattern.length} characters`);
                }

                totalValidationErrors(passwordError);

                if (passwordError?.length) {
                    return { passwordPattern: true };
                }
                return null;
            }), tap(afterAction)).pipe(first());
    };
